import * as React from 'react';
import Popconfirm from 'antd/lib/popconfirm';
import PermissionedActionButton from './HOCs/PermissionedActionButton';
import { displayUserNameLong } from '../helpers/displayHelpers';
import { useDispatch } from 'react-redux';
import { DeleteOutlined } from '@ant-design/icons';
import { OrgSettingsUpdatable } from '../actions/updatable';
import { RunwayUserAction } from '../helpers/aclHelpers';
import { RemoveOrgUserRequest, tryUpdateOrgSettings } from '../actions/orgSettings';
import { User } from '../types';
import { antRed } from './styles/globalStyles';
import { globalMessage } from '../utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface RemoveUserButtonProps {
    selectedUser?: User;
    user: User;
    orgId: string;
    isDirectorySyncEnabled: boolean;
    onUserRemoved: () => void;
}

const RemoveUserButton: React.FC<RemoveUserButtonProps> = (props: RemoveUserButtonProps) => {
    const { selectedUser, user, orgId, isDirectorySyncEnabled } = props;
    const dispatch = useDispatch();
    const isError = useError(OrgSettingsUpdatable.RemoveOrgUser);
    const isLoading = useIsLoading(OrgSettingsUpdatable.RemoveOrgUser);
    const isSuccess = useUpdated(OrgSettingsUpdatable.RemoveOrgUser);
    const [isPopconfirmVisible, setIsPopconfirmVisible] = React.useState(false);

    const [confirmDeleteText, setConfirmDeleteText] = React.useState(null);

    const deleteUser = async () => {
        const requestData: RemoveOrgUserRequest = {
            orgId,
            userId: selectedUser.id,
        };
        dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.RemoveOrgUser, requestData));
    };

    React.useEffect(() => {
        if (isError) {
            globalMessage.error(isError);
        }
    }, [isError]);

    React.useEffect(() => {
        if (isSuccess) {
            setIsPopconfirmVisible(false);
            globalMessage.success('User successfully removed');

            props.onUserRemoved();
        }
    }, [isSuccess]);

    React.useEffect(() => {
        if (selectedUser) {
            setConfirmDeleteText(`Are you sure you want to remove user  ` + displayUserNameLong(selectedUser) + '?');
        }
    }, [selectedUser]);

    return (
        <Popconfirm
            open={isPopconfirmVisible}
            placement="left"
            title={confirmDeleteText}
            onConfirm={deleteUser}
            okText="Yes, remove"
            okButtonProps={{ loading: isLoading }}
            cancelText="No"
            onCancel={() => setIsPopconfirmVisible(false)}
        >
            <PermissionedActionButton
                type="link"
                icon={<DeleteOutlined style={{ color: antRed.red4 }} />}
                style={{ color: antRed.red5, display: 'flex', alignItems: 'center' }}
                actionType={RunwayUserAction.UARemoveOrgUser}
                disabled={isDirectorySyncEnabled || (selectedUser ? user.id === selectedUser.id : false)}
                onClick={() => setIsPopconfirmVisible(true)}
            >
                Remove user
            </PermissionedActionButton>
        </Popconfirm>
    );
};

export default RemoveUserButton;
